import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, API } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { Button } from "@/components/ui/button";
import { Check, Download, Phone, Calendar, Share2, ArrowRight, FileText, Clock, Mail, Copy, Link } from "lucide-react";
import AppointmentModal from "../components/AppointmentModal";
import { toast } from "sonner";
import { brandConfig } from "@/lib/brandConfig";

const LockedOfferScreen = ({ state, resetState }) => {
  const navigate = useNavigate();
  const { lockedOffer, offer, vehicleInfo, contactInfo } = state;
  const [showPrice, setShowPrice] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { isCompact: compact, isEmbedded } = useWidgetMode();

  useEffect(() => {
    if (!lockedOffer) {
      navigate('/');
      return;
    }
    const timer = setTimeout(() => setShowPrice(true), 300);
    return () => clearTimeout(timer);
  }, [lockedOffer, navigate]);

  if (!lockedOffer || !offer || !vehicleInfo) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0 
    }).format(value);
  };

  const validDate = new Date(lockedOffer.valid_until).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const adjustmentColor = lockedOffer.total_adjustment >= 0 ? 'text-emerald-600' : 'text-red-600';

  const shareUrl = `${window.location.origin}/offer/view/${lockedOffer.locked_offer_id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard!");
    } catch {
      // Fallback for older browsers
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      toast.success("Share link copied!");
    }
  };

  const handleNativeShare = async () => {
    const text = `Check out my ${brandConfig.name} offer for my ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}: ${formatCurrency(lockedOffer.locked_offer)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `My ${brandConfig.name} Offer`, text, url: shareUrl });
      } catch {
        // Expected: user cancelled the native share dialog
      }
    } else {
      handleCopyLink();
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/offers/${offer.offer_id}/pdf`);
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${brandConfig.name}_Offer_${vehicleInfo.year}_${vehicleInfo.make}_${vehicleInfo.model}.pdf`.replace(/ /g, "_");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={6} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Success Header */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-8"}`}>
            <div className={`bg-emerald-100 rounded-full flex items-center justify-center mx-auto ${compact ? "w-10 h-10 mb-2" : "w-16 h-16 mb-4"}`}>
              <Check className={compact ? "w-5 h-5 text-emerald-600" : "w-8 h-8 text-emerald-600"} />
            </div>
            <h1 className={compact ? "text-xl font-medium tracking-tight text-slate-900 mb-1" : "text-heading-2 mb-2"}>
              Your Offer is Locked!
            </h1>
            <p className={compact ? "text-sm text-slate-500" : "text-body"}>
              Congratulations! Here's your final cash offer.
            </p>
          </div>

          {/* Locked Offer Card */}
          <div className={`surface-elevated text-center opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "p-4" : "p-8 sm:p-12"}`}>
            {/* Vehicle Info */}
            <p className="text-slate-600 mb-2">Locked offer for</p>
            <h2 className={compact ? "text-lg font-semibold text-slate-900 mb-3" : "text-heading-3 mb-6"}>
              {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
            </h2>

            {/* Original Offer */}
            <div className={compact ? "mb-2" : "mb-4"}>
              <p className="text-sm text-slate-500">Original Offer</p>
              <p className="text-2xl text-slate-400 line-through">
                {formatCurrency(lockedOffer.original_offer)}
              </p>
            </div>

            {/* Locked Price */}
            <div className={`${compact ? "py-3" : "py-6"} ${showPrice ? 'animate-price-reveal' : 'opacity-0'}`}>
              <p className="text-label text-emerald-600 mb-2">YOUR LOCKED OFFER</p>
              <p className="offer-price text-emerald-600" data-testid="locked-offer-price">
                {formatCurrency(lockedOffer.locked_offer)}
              </p>
            </div>

            {/* Adjustment Summary */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full ${compact ? "mb-3" : "mb-6"}`}>
              <span className="text-sm text-slate-600">Condition Adjustment:</span>
              <span className={`font-semibold ${adjustmentColor}`}>
                {lockedOffer.total_adjustment >= 0 ? '+' : ''}{formatCurrency(lockedOffer.total_adjustment)}
              </span>
            </div>

            {/* Valid Until */}
            <div className={`flex items-center justify-center gap-2 text-slate-600 ${compact ? "mb-4" : "mb-8"}`}>
              <Clock className="w-5 h-5" />
              <span>Valid until {validDate}</span>
            </div>

            {/* Offer ID */}
            <div className={`bg-slate-50 rounded-xl p-4 ${compact ? "mb-4" : "mb-8"}`}>
              <p className="text-label mb-1">OFFER REFERENCE</p>
              <p className="font-mono text-sm text-slate-900" data-testid="locked-offer-id">
                {lockedOffer.locked_offer_id}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className={`flex flex-col @sm:flex-row max-w-md mx-auto ${compact ? "gap-2" : "gap-4"}`}>
              {/* Schedule Appointment is Nucar-specific (AppointmentModal's location
                  picker is backed by the hardcoded NUCAR_LOCATIONS list) — only shown
                  on the master nucarnext.com standalone site, not third-party embeds. */}
              {!isEmbedded && (
                <Button
                  className={`flex-1 ${fieldHeight} btn-accent`}
                  onClick={() => setShowAppointment(true)}
                  data-testid="schedule-appointment-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Appointment
                </Button>
              )}
              {brandConfig.phone && (
                <Button
                  asChild
                  variant="outline"
                  className={`flex-1 ${fieldHeight} btn-secondary`}
                  data-testid="call-us-btn"
                >
                  <a href={`tel:${brandConfig.phone.replace(/[^\d+]/g, '')}`}>
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us
                  </a>
                </Button>
              )}
            </div>

            {/* Email sent indicator */}
            {contactInfo?.email && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
                <Mail className="w-4 h-4" />
                <span>Offer details sent to {contactInfo.email}</span>
              </div>
            )}
          </div>

          {/* Share & Download Actions */}
          <div className={`flex flex-col @sm:flex-row gap-3 opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "mt-3" : "mt-6"}`}>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {downloading ? "Generating..." : "Download PDF"}
            </button>
            <button
              onClick={handleCopyLink}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
              data-testid="copy-share-link-btn"
            >
              <Link className="w-4 h-4" />
              Copy Share Link
            </button>
            <button
              onClick={handleNativeShare}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
              data-testid="share-offer-btn"
            >
              <Share2 className="w-4 h-4" />
              Share Offer
            </button>
          </div>

          {/* Adjustment Breakdown */}
          <div className={`surface-card opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "mt-3 p-4" : "mt-8 p-6"}`}>
            <h3 className="font-semibold text-slate-900 mb-4">Adjustment Breakdown</h3>
            <div className="space-y-3">
              {lockedOffer.adjustments.map((adj) => (
                <div key={adj.question} className="flex justify-between items-start text-sm">
                  <div>
                    <p className="font-medium text-slate-900">{adj.question}</p>
                    <p className="text-slate-500">{adj.answer}</p>
                  </div>
                  <span className={`font-medium ${adj.adjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {adj.adjustment >= 0 ? '+' : ''}{formatCurrency(adj.adjustment)}
                  </span>
                </div>
              ))}
              <div className="pt-3 border-t border-slate-200 flex justify-between font-semibold">
                <span>Total Adjustment</span>
                <span className={adjustmentColor}>
                  {lockedOffer.total_adjustment >= 0 ? '+' : ''}{formatCurrency(lockedOffer.total_adjustment)}
                </span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className={`bg-blue-50 rounded-2xl opacity-0 animate-fade-in-up animate-stagger-5 ${compact ? "mt-3 p-4" : "mt-8 p-6"}`}>
            <h3 className="font-semibold text-slate-900 mb-3">Next Steps</h3>
            <ol className="space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>{brandConfig.name} will be in touch soon</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span>Prepare your vehicle for a quick inspection</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Get paid the same day!</span>
              </li>
            </ol>
          </div>

          {/* Start Over */}
          <div className={compact ? "mt-4 text-center" : "mt-8 text-center"}>
            <Button
              variant="ghost"
              onClick={() => {
                resetState();
                navigate('/');
              }}
              className="text-slate-500 hover:text-slate-700"
              data-testid="start-over-btn"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Get Another Offer
            </Button>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}

      {/* Appointment Scheduling Modal */}
      <AppointmentModal
        open={showAppointment}
        onClose={() => setShowAppointment(false)}
        offerId={offer?.offer_id}
        lockedOfferId={lockedOffer?.locked_offer_id}
        existingContact={contactInfo}
      />
    </div>
  );
};

export default LockedOfferScreen;
